import { useState, useEffect } from 'react'
import { doc, onSnapshot, getDoc, Timestamp, collection, query, where, getDocs, limit } from 'firebase/firestore'
import { db } from '@/lib/firebase'
import { useAuthState } from './useAuth'
import { checkIsAdminModeActive } from './useAdminMode'
import type { UserData, Artist } from '@/types/models'

export type UserType = 'client' | 'artist' | 'booking_artist'
export type BookingSubscriptionStatus = 'active' | 'inactive' | 'cancelled' | 'expired' | 'trial'

interface BookingSubscription {
  startDate: Timestamp
  status: BookingSubscriptionStatus
  includeFakeChats?: boolean
}

export interface CachedUserProfile {
  uid?: string
  isArtist: boolean
  artistId: string | null
  firstName?: string
  lastName?: string
  displayName?: string
  instagram?: string
  email?: string
  hasBookingSystem: boolean
  bookingStatus: BookingSubscriptionStatus | null
  timestamp: number
}

async function resolveArtistIdForUser(uid: string, data: UserData): Promise<string | null> {
  if (data.artistId) return data.artistId
  if (data.isArtist !== true) return null

  try {
    const artistQuery = query(
      collection(db, 'artists'),
      where('userId', '==', uid),
      limit(1)
    )
    const snapshot = await getDocs(artistQuery)
    if (!snapshot.empty) {
      const resolvedId = snapshot.docs[0].id
      console.log('[useUserData] Resolved artistId from artists.userId:', resolvedId)
      return resolvedId
    }
  } catch (err) {
    console.error('[useUserData] Error resolving artistId from userId:', err)
  }

  return null
}

export function useUserData() {
  const { user } = useAuthState()
  const [userData, setUserData] = useState<UserData | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<Error | null>(null)
  const [bookingSubscription, setBookingSubscription] = useState<BookingSubscription | null>(null)
  const [bookingLoading, setBookingLoading] = useState(false)

  useEffect(() => {
    if (!user?.uid) {
      setUserData(null)
      setLoading(false)
      return
    }

    setLoading(true)
    const userDocRef = doc(db, 'users', user.uid)
    
    const unsubscribe = onSnapshot(userDocRef, async (userDoc) => {
      if (userDoc.exists()) {
        const data = userDoc.data() as UserData
        const resolvedArtistId = await resolveArtistIdForUser(user.uid, data)
        let enhancedUserData: UserData = {
          ...data,
          uid: user.uid,
          ...(resolvedArtistId ? { artistId: resolvedArtistId } : {}),
        }

        // If user is an artist, fetch artist data and attach it
        if (enhancedUserData.artistId) {
          try {
            const artistDocRef = doc(db, 'artists', enhancedUserData.artistId)
            const artistDoc = await getDoc(artistDocRef)
            
            if (artistDoc.exists()) {
              const artistData = artistDoc.data() as Artist
              enhancedUserData = {
                ...enhancedUserData,
                displayName: artistData.displayName,
                firstName: artistData.firstName,
                lastName: artistData.lastName,
              }
              console.log('[useUserData] Artist data attached:', {
                displayName: artistData.displayName,
                firstName: artistData.firstName,
                lastName: artistData.lastName
              })
            }
          } catch (err) {
            console.error('[useUserData] Error fetching artist data:', err)
            // Continue with user data even if artist fetch fails
          }
        }

        setUserData(enhancedUserData)
        setLoading(false)
        
        // Update comprehensive cache (will be enhanced with booking data later if artist)
        updateCache(enhancedUserData, null, user.uid)
        
        console.log('[useUserData] User data loaded:', { 
          uid: enhancedUserData.uid, 
          isArtist: enhancedUserData.isArtist, 
          artistId: enhancedUserData.artistId,
          displayName: enhancedUserData.displayName
        })
      } else {
        // Create basic user data if it doesn't exist
        const basicUserData: UserData = {
          uid: user.uid,
          email: user.email || '',
          createdAt: new Date(),
          lastSignIn: new Date()
        }
        setUserData(basicUserData)
        setLoading(false)
        
        // Clear cached profile if user doc doesn't exist (user not yet set up as artist)
        updateCache(basicUserData, null, user.uid)
      }
    }, (err) => {
      console.error('[useUserData] Error fetching user data:', err)
      setError(err)
      setLoading(false)
    })

    return unsubscribe
  }, [user?.uid])

  // Helper function to update comprehensive cache
  const updateCache = (userData: UserData, subscription: BookingSubscription | null, uid: string) => {
    const hasActiveBooking = subscription?.status === 'active' || subscription?.status === 'trial'
    const isArtistUser = userData.isArtist === true || !!userData.artistId
    
    const cachedProfile: CachedUserProfile = {
      uid,
      isArtist: isArtistUser,
      artistId: userData.artistId || null,
      firstName: userData.firstName,
      lastName: userData.lastName,
      displayName: userData.displayName,
      instagram: userData.instagram,
      email: userData.email,
      hasBookingSystem: hasActiveBooking,
      bookingStatus: subscription?.status || null,
      timestamp: Date.now()
    }
    
    // Store in both old key (for backward compatibility) and new key
    localStorage.setItem('cachedUserRole', JSON.stringify({
      isArtist: cachedProfile.isArtist,
      artistId: cachedProfile.artistId,
      timestamp: cachedProfile.timestamp
    }))
    
    localStorage.setItem('cachedUserProfile', JSON.stringify(cachedProfile))
    
    console.log('[useUserData] Cache updated:', {
      isArtist: cachedProfile.isArtist,
      hasBookingSystem: cachedProfile.hasBookingSystem,
      bookingStatus: cachedProfile.bookingStatus
    })
  }

  // Lazy-load booking system status (only for artists)
  useEffect(() => {
    const isArtistUser = userData?.isArtist === true || !!userData?.artistId
    if (!isArtistUser || !userData?.artistId) {
      // Not an artist, clear booking data
      setBookingSubscription(null)
      setBookingLoading(false)
      return
    }

    console.log('[useUserData] Artist detected, subscribing to booking system status for:', userData.artistId)
    setBookingLoading(true)

    const bookingSystemRef = doc(db, 'bookingSystems', userData.artistId)
    
    const unsubscribe = onSnapshot(
      bookingSystemRef,
      (docSnap) => {
        if (docSnap.exists()) {
          const data = docSnap.data()
          const subscription = data?.bookingSubscription as BookingSubscription | undefined
          
          if (subscription) {
            console.log('[useUserData] Booking subscription loaded:', subscription.status)
            setBookingSubscription(subscription)
            
            // Update cache with booking system data
            updateCache(userData, subscription, user!.uid)
          } else {
            console.log('[useUserData] No booking subscription found')
            setBookingSubscription(null)
            updateCache(userData, null, user!.uid)
          }
        } else {
          console.log('[useUserData] No booking system document found')
          setBookingSubscription(null)
          updateCache(userData, null, user!.uid)
        }
        
        setBookingLoading(false)
      },
      (err) => {
        console.error('[useUserData] Error fetching booking system:', err)
        setBookingSubscription(null)
        setBookingLoading(false)
      }
    )

    return unsubscribe
  }, [userData?.isArtist, userData?.artistId])

  // Get cached profile for instant initial render
  const getCachedProfile = (): CachedUserProfile | null => {
    try {
      const cached = localStorage.getItem('cachedUserProfile')
      if (!cached) return null
      const profile = JSON.parse(cached) as CachedUserProfile
      if (user?.uid) {
        if (!profile.uid || profile.uid !== user.uid) {
          return null
        }
      }
      return profile
    } catch {
      return null
    }
  }

  // Computed properties — artistId alone implies artist (legacy / onboarding docs)
  const isArtist = userData?.isArtist === true || !!(userData?.artistId)
  const artistId = userData?.artistId || getCachedProfile()?.artistId || null
  const hasActiveBooking = bookingSubscription?.status === 'active' || bookingSubscription?.status === 'trial'
  const hasBookingSystem = hasActiveBooking
  
  // User type determination
  const getUserType = (): UserType => {
    if (!isArtist) return 'client'
    return hasActiveBooking ? 'booking_artist' : 'artist'
  }
  
  const userType = getUserType()
  const isClient = userType === 'client'
  const isBookingArtist = userType === 'booking_artist'

  // Helper function to check if the user is viewing their own artist page
  const isOwnArtistPage = (pageArtistId: string | undefined): boolean => {
    if (!pageArtistId || !artistId) {
      return false
    }
    return artistId === pageArtistId
  }

  // Helper function to check if admin mode is active
  // Returns true only if user has isAdmin AND admin mode toggle is enabled
  const isAdminModeActive = checkIsAdminModeActive(userData?.isAdmin)

  return { 
    userData, 
    loading, 
    error,
    // Computed role properties
    isArtist,
    artistId,
    isClient,
    isBookingArtist,
    userType,
    // User profile properties
    displayName: userData?.displayName || null,
    // Booking system properties
    hasBookingSystem,
    bookingSubscription,
    bookingLoading,
    // Helpers
    isOwnArtistPage,
    isAdminModeActive,
    getCachedProfile
  }
}